import React from 'react';
import {
  Box,
  Typography,
  Container,
  Grid as MuiGrid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  PictureAsPdf as PdfIcon,
  VideoLibrary as VideoIcon,
  Description as DocIcon,
  Download as DownloadIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';

const MotionCard = motion.create(Card);
const Grid = MuiGrid as any;

interface Document {
  title: string;
  type: 'pdf' | 'video' | 'document';
  thumbnail: string;
  description: string;
  url: string;
  published: string;
}

const documents: Document[] = [
  {
    title: 'Resume',
    type: 'pdf',
    thumbnail: '/images/resume.jpg',
    description: 'My current resume in PDF format',
    url: '/docs/Michael-Suliot-Resume-20241027.pdf',
    published: 'Oct 27, 2024',
  },
];

const DocumentCard = ({ document }: { document: Document }) => {
  const getIcon = () => {
    switch (document.type) {
      case 'pdf':
        return <PdfIcon />;
      case 'video':
        return <VideoIcon />;
      case 'document':
        return <DocIcon />;
      default:
        return <DocIcon />;
    }
  };

  return (
    <MotionCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.paper',
        maxWidth: 400,
        mx: 'auto',
      }}
    >
      {/* Image area with cross-browser consistency */}
      <Box
        sx={{
          width: '100%',
          height: 220,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <CardMedia
          component="img"
          image={document.thumbnail}
          alt={document.title}
          sx={{
            maxHeight: '200px',
            maxWidth: '90%',
            objectFit: 'contain',
          }}
        />
      </Box>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getIcon()}
          <Typography variant="h6" component="h2" sx={{ ml: 1 }}>
            {document.title}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" gutterBottom>
          Published: {document.published}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {document.description}
        </Typography>
      </CardContent>
      <CardActions>
        {document.type === 'video' ? (
          <Button
            startIcon={<PlayIcon />}
            href={document.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Watch
          </Button>
        ) : (
          <Button
            startIcon={<DownloadIcon />}
            href={document.url}
            download
          >
            Download
          </Button>
        )}
      </CardActions>
    </MotionCard>
  );
};

const Documents = () => {
  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Documents & Media
        </Typography>
        
        {/* Centered, max-width grid for desktop */}
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Grid container spacing={4} justifyContent="center">
            {documents.map((doc, index) => (
              <Grid key={index} sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                <DocumentCard document={doc} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default Documents; 